import { cn } from '@endure/ui';

import { Icon } from 'components';
import { Link } from 'containers/Link';
import { useTranslation } from 'libs/translations';
import { matchPath, useLocation } from 'react-router';
import type { MenuItem, SubMenu } from '../../../types';

export interface SubMenuItemProps extends MenuItem {
  variant?: SubMenu['variant'];
  chromeVariant?: 'dark' | 'light';
  onClick?: () => void;
}

export const SubMenuItem: React.FC<SubMenuItemProps> = ({
  to,
  href,
  label,
  description,
  imgSrc,
  iconName,
  onClick,
  variant,
}) => {
  const { t } = useTranslation();

  const { pathname } = useLocation();

  const isActive = to && !!matchPath(to, pathname);
  const linkNavProps = to ? { to } : { href };

  return (
    <Link
      {...linkNavProps}
      onClick={onClick}
      className={cn(
        'forge-submenu-item block py-3 space-y-6 rounded-lg group transition-colors hover:no-underline',
        'forge-submenu-item--light',
        variant === 'secondary' ? 'px-6' : 'px-4 bg-elevated hover:bg-surface-2',
        isActive && variant === 'primary' && 'bg-surface-2',
      )}
    >
      <div className="flex gap-x-3">
        {(iconName || imgSrc) && (
          <div
            className={cn(
              'rounded-lg size-12 shrink-0 flex items-center justify-center',
              !!iconName && 'bg-surface-2',
            )}
          >
            {iconName ? (
              <Icon
                name={iconName}
                className={cn(
                  'size-6 transition-colors',
                  'text-muted group-hover:text-foreground',
                  isActive && 'text-foreground',
                )}
              />
            ) : (
              <img src={imgSrc} alt={label} className="size-12" />
            )}
          </div>
        )}

        <div className="flex flex-col">
          <p
            className={cn(
              'font-semibold',
              'text-foreground transition-colors group-hover:text-accent-strong',
              isActive && 'text-foreground',
            )}
          >
            {label}
          </p>

          {!!description && <p className="text-xs text-muted">{description}</p>}
        </div>
      </div>

      {variant === 'secondary' && (
        <div
          className={cn(
            'rounded-lg border inline-block px-3 py-2 transition-colors',
            'border-line text-foreground group-hover:bg-surface-2',
            isActive && 'bg-surface-2',
          )}
        >
          {t('layout.menu.subMenuItem.button.label')}
        </div>
      )}
    </Link>
  );
};
